import React from 'react';
import { motion } from 'framer-motion';
import { Section, FadeIn } from './ui';
import { MapPinIcon, UsersIcon, TargetIcon, CheckCircle2Icon, SparklesIcon, BarChart3Icon, TrendingUpIcon, BellIcon } from 'lucide-react';
export function LiveDashboard() {
  return <Section className="bg-slate-50 relative overflow-hidden" data-id="element-4446">
      <div className="absolute inset-0 pointer-events-none" data-id="element-4447">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-brand-teal/[0.06] rounded-full blur-3xl" data-id="element-4448" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-brand-coral/[0.05] rounded-full blur-3xl" data-id="element-4449" />
      </div>

      <div className="relative z-10 grid lg:grid-cols-12 gap-12 items-center" data-id="element-4450">
        <div className="lg:col-span-5" data-id="element-4451">
          <FadeIn data-id="element-4452">
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-1 pr-4 py-1 mb-6 shadow-sm" data-id="element-4453">
              <span className="bg-brand-teal/10 text-brand-teal text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider" data-id="element-4454">
                Planner Dashboard
              </span>
              <span className="text-sm font-medium text-slate-600" data-id="element-4455">
                Real-time visibility
              </span>
            </div>
          </FadeIn>
          <FadeIn delay={0.05} data-id="element-4456">
            <h2 className="text-3xl md:text-5xl font-bold text-brand-navy leading-tight mb-5 text-balance" data-id="element-4457">
              Watch your offsite move from{' '}
              <span className="text-brand-teal" data-id="element-4458">planning to proof</span> in one
              workspace.
            </h2>
          </FadeIn>
          <FadeIn delay={0.15} data-id="element-4459">
            <p className="text-lg text-slate-600 leading-relaxed" data-id="element-4460">
              Track intake completion, engagement, AI agenda readiness, and
              post-event reporting from a single live dashboard. Every event
              moves through a clean Before · During · After timeline.
            </p>
          </FadeIn>
        </div>

        <div className="lg:col-span-7 relative lg:h-[680px] flex items-center justify-center" data-id="element-4461">
          <div className="absolute inset-0 bg-gradient-to-br from-brand-teal/10 via-transparent to-brand-coral/10 rounded-full blur-3xl" data-id="element-4462" />

          {/* Floating notification — top right */}
          <motion.div initial={{
          opacity: 0,
          x: 30,
          y: -10
        }} whileInView={{
          opacity: 1,
          x: 0,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="hidden md:flex absolute -top-2 -right-2 lg:right-4 z-20 bg-white rounded-xl shadow-xl border border-slate-100 p-3 items-center gap-3 max-w-[240px]" data-id="element-4463">
            <div className="bg-brand-teal/10 p-2 rounded-lg shrink-0" data-id="element-4464">
              <BellIcon className="w-4 h-4 text-brand-teal" data-id="element-4465" />
            </div>
            <div className="min-w-0" data-id="element-4466">
              <div className="text-xs font-bold text-brand-navy" data-id="element-4467">
                New intake response
              </div>
              <div className="text-xs text-slate-500 truncate" data-id="element-4468">
                Sarah K. • 2s ago
              </div>
            </div>
            <span className="w-2 h-2 rounded-full bg-brand-coral animate-pulse shrink-0" data-id="element-4469" />
          </motion.div>

          {/* Floating mini-stat — bottom left */}
          <motion.div initial={{
          opacity: 0,
          x: -30,
          y: 10
        }} whileInView={{
          opacity: 1,
          x: 0,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="hidden md:flex absolute -bottom-4 -left-4 lg:left-0 z-20 bg-white rounded-xl shadow-xl border border-slate-100 p-4 items-center gap-3" data-id="element-4470">
            <div className="bg-emerald-50 p-2 rounded-lg" data-id="element-4471">
              <TrendingUpIcon className="w-5 h-5 text-emerald-600" data-id="element-4472" />
            </div>
            <div data-id="element-4473">
              <div className="text-xs font-medium text-slate-500" data-id="element-4474">
                Connection Score
              </div>
              <div className="flex items-baseline gap-1" data-id="element-4475">
                <span className="text-lg font-bold text-brand-navy" data-id="element-4476">8.1</span>
                <span className="text-xs font-semibold text-emerald-600" data-id="element-4477">
                  +1.7
                </span>
              </div>
            </div>
          </motion.div>

          {/* Main dashboard card */}
          <motion.div initial={{
          y: 40,
          opacity: 0
        }} whileInView={{
          y: 0,
          opacity: 1
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.8
        }} className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl shadow-slate-300/40 border border-slate-100 overflow-hidden flex flex-col z-10" data-id="element-4478">
            <div className="bg-brand-navy text-white p-5 flex justify-between items-start relative overflow-hidden" data-id="element-4479">
              <div className="absolute top-0 right-0 w-40 h-40 bg-brand-teal/10 rounded-full blur-2xl" data-id="element-4480" />
              <div className="relative z-10" data-id="element-4481">
                <div className="flex items-center gap-2 mb-1.5" data-id="element-4482">
                  <span className="flex items-center gap-1.5 bg-emerald-500/15 text-emerald-300 text-xs font-semibold px-2 py-0.5 rounded-full" data-id="element-4483">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-4484" />
                    Active Event
                  </span>
                  <span className="flex items-center text-xs text-slate-300" data-id="element-4485">
                    <MapPinIcon className="w-3 h-3 mr-1" data-id="element-4486" /> Punta Cana
                  </span>
                </div>
                <h3 className="text-xl font-bold" data-id="element-4487">Leadership Reset Retreat</h3>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-2 text-center min-w-[60px] relative z-10" data-id="element-4488">
                <UsersIcon className="w-5 h-5 mx-auto mb-1 text-brand-coral" data-id="element-4489" />
                <span className="text-sm font-bold" data-id="element-4490">48</span>
              </div>
            </div>

            <div className="p-6 space-y-5 bg-slate-50/50 flex-1" data-id="element-4491">
              <div className="grid grid-cols-2 gap-4" data-id="element-4492">
                <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm" data-id="element-4493">
                  <div className="flex justify-between items-center mb-2" data-id="element-4494">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-4495">
                      Intake
                    </span>
                    <span className="text-sm font-bold text-brand-navy" data-id="element-4496">
                      86%
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden" data-id="element-4497">
                    <motion.div className="bg-brand-teal h-2 rounded-full" initial={{
                    width: 0
                  }} whileInView={{
                    width: '86%'
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1.2,
                    delay: 0.4
                  }} data-id="element-4498" />
                  </div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm" data-id="element-4499">
                  <div className="flex justify-between items-center mb-2" data-id="element-4500">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-4501">
                      Engagement
                    </span>
                    <span className="text-sm font-bold text-brand-navy" data-id="element-4502">
                      78%
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden" data-id="element-4503">
                    <motion.div className="bg-brand-coral h-2 rounded-full" initial={{
                    width: 0
                  }} whileInView={{
                    width: '78%'
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1.2,
                    delay: 0.6
                  }} data-id="element-4504" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm" data-id="element-4505">
                <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700" data-id="element-4506">
                  <TargetIcon className="w-4 h-4 text-brand-teal" data-id="element-4507" />
                  Objective: Team Alignment + Wellness
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-4508">
                  {['Communication', 'Trust', 'Energy', 'Leadership'].map(g => <span key={g} className="bg-slate-100 text-slate-600 text-xs px-2.5 py-1 rounded-md font-medium" data-id="element-4509">
                        {g}
                      </span>)}
                </div>
              </div>

              <div className="space-y-3" data-id="element-4510">
                <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-100 shadow-sm" data-id="element-4511">
                  <div className="flex items-center gap-3" data-id="element-4512">
                    <div className="bg-brand-coral/10 p-2 rounded-md" data-id="element-4513">
                      <SparklesIcon className="w-4 h-4 text-brand-coral" data-id="element-4514" />
                    </div>
                    <span className="text-sm font-medium text-slate-700" data-id="element-4515">
                      AI Agenda
                    </span>
                  </div>
                  <span className="flex items-center text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full" data-id="element-4516">
                    <CheckCircle2Icon className="w-3 h-3 mr-1" data-id="element-4517" /> Ready
                  </span>
                </div>
                <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-100 shadow-sm" data-id="element-4518">
                  <div className="flex items-center gap-3" data-id="element-4519">
                    <div className="bg-brand-navy/10 p-2 rounded-md" data-id="element-4520">
                      <BarChart3Icon className="w-4 h-4 text-brand-navy" data-id="element-4521" />
                    </div>
                    <span className="text-sm font-medium text-slate-700" data-id="element-4522">
                      Post-Event Report
                    </span>
                  </div>
                  <span className="text-xs font-semibold text-brand-teal bg-brand-teal/10 px-2 py-1 rounded-full" data-id="element-4523">
                    Preview Available
                  </span>
                </div>
              </div>

              <div className="pt-2" data-id="element-4524">
                <div className="flex justify-between relative" data-id="element-4525">
                  <div className="absolute top-2 left-0 w-full h-0.5 bg-slate-200 z-0" data-id="element-4526" />
                  <motion.div className="absolute top-2 left-0 h-0.5 bg-brand-teal z-0" initial={{
                  width: 0
                }} whileInView={{
                  width: '50%'
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 1,
                  delay: 0.8
                }} data-id="element-4527" />
                  {['Before', 'During', 'After'].map((step, i) => <div key={step} className="relative z-10 flex flex-col items-center gap-1.5" data-id="element-4528">
                      <div className={`w-4 h-4 rounded-full border-2 ${i === 0 ? 'bg-brand-teal border-brand-teal' : i === 1 ? 'bg-white border-brand-teal' : 'bg-white border-slate-300'}`} data-id="element-4529" />
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${i <= 1 ? 'text-brand-navy' : 'text-slate-400'}`} data-id="element-4530">
                        {step}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </Section>;
}